<template>
  <main>
    <div data-scroll-el="pdpContainer" />
    <div>
      <slot name="notifications" />
      <transition name="fade">
        <div v-if="productPending && !product">
          <pdp-skeleton />
        </div>
        <div v-else-if="product">
          <div class="lg:mb-10 lg:flex lg:items-start">
            <section class="relative lg:grow" data-scroll-el="gallery">
              <template v-if="galleryPending">
                <div class="lg:grid lg:cols-2 lg:grow lg:gap-2 <lg:space-y-1">
                  <div
                    v-for="i in 6"
                    :key="i"
                    class="skeleton"
                    :style="{ 'aspect-ratio': presets.src.width / presets.src.height }"
                  />
                </div>
              </template>
              <slot v-else name="gallery" />
            </section>
            <div
              v-style="{ w: { lg: '25.5rem' }, top: { lg: pxToRem(header.height.header) } }"
              class="px-4 pt-4 lg:sticky lg:top-0 lg:shrink-0 lg:pt-6"
            >
              <pdp-section-heading v-if="productPrice" class="mb-4">
                <template #reviews-summary>
                  <slot name="reviews-summary" />
                </template>
              </pdp-section-heading>
              <slot name="espot-below-pricing" />
              <pdp-section-gift-card v-if="isDigitalProduct" />
              <pdp-section-variant-selection v-else>
                <template #size-chart>
                  <slot name="size-chart" />
                </template>
                <template #size-advice>
                  <slot name="size-advice" />
                </template>
              </pdp-section-variant-selection>
              <slot name="espot-below-sizes" />
              <slot name="bundle-upsells" />
              <div v-if="!isDigitalProduct" class="mt-4 f-col">
                <div class="flex gap-0.5">
                  <div class="min-w-0 grow">
                    <slot name="cta-add-to-cart" />
                  </div>
                  <slot name="cta-favorites" />
                </div>
                <slot name="cta-apple-pay" />
                <p v-if="showLegalDisclaimer && allowApplePayOrderOnPDP" class="mt-2 text-xs">
                  <form-legal-disclaimer
                    :content="$t.checkoutLegalText"
                    :data-test-ids="{
                      'sale-terms': 'pdp-terms-link',
                      'privacy': 'pdp-privacy-link',
                    }"
                  />
                </p>
                <slot name="klarna-payments" />
                <slot name="paypal-payments" />
                <slot name="cta-reserve-in-store" />
                <slot name="find-in-store" />
              </div>
              <div v-else class="mt-4 f-col gap-2 lg:mt-6">
                <slot name="klarna-payments" />
                <slot name="paypal-payments" />
              </div>
              <slot name="shipping-methods" />
              <slot name="upsells" />
            </div>
          </div>
          <div class="pb-6 space-y-6 lg:pb-8 lg:space-y-8">
            <div class="mb-8 px-4 lg:px-6">
              <slot name="espot-top-of-secondary-section" />
              <slot name="accordions" />
              <slot name="espot-below-accordions" />
            </div>
            <slot name="shop-the-look" />
            <slot name="espot-lazy-above-footer" />
            <slot v-if="!product?.dummyCustoms" name="reviews" />
          </div>
        </div>
      </transition>
      <slot name="recommendations" />
    </div>
    <slot name="sticky-cta-bar" />
    <slot />
  </main>
</template>

<script lang="ts" setup>
import type { Slots } from '#commerce/types/pdp'

defineSlots<Slots>()

const header = useHeaderStore()
const { isDigitalProduct, product, productPending, productPrice } = usePdp()
const { galleryPending } = usePdp()
const { allowApplePayOrderOnPDP } = useExpressPaymentFeatures()
const { gallery: { presets }, showLegalDisclaimer } = useAppConfig().pages.pdp
</script>
